/**
 * Frontend Sentry Configuration
 * Mirrors backend pattern in src/utils/sentry.ts
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react-router/
 */

import * as Sentry from '@sentry/react-router';

// Types
interface SentryClientConfig {
  dsn: string;
  environment: 'development' | 'staging' | 'production';
  release?: string;
}

/**
 * Get Sentry configuration options for frontend
 * Environment-aware with matching sample rates to backend
 */
export function getSentryClientOptions(config: SentryClientConfig): Sentry.BrowserOptions {
  const { dsn, environment, release } = config;

  return {
    dsn,
    environment,
    release,

    // Error tracking: 100% capture
    // Performance: Match backend rates (staging=100%, prod=10%)
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Session Replay disabled (can enable later)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Don't send PII
    sendDefaultPii: false,

    // Ignore noise (same as backend + hydration errors)
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection',
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // Hydration errors are intentional due to ClientOnly pattern
      'Hydration',
      'hydrat',
      'Text content does not match',
      'did not match',
      '#418',
      '#423',
      '#425',
    ],

    // React Router tracing integration (no hooks needed for @sentry/react-router)
    integrations: [
      Sentry.reactRouterTracingIntegration(),
    ],

    // Runtime tag
    initialScope: {
      tags: {
        runtime: 'react-router-client',
      },
    },
  };
}

/**
 * Initialize Sentry for frontend
 * Call once in entry.client.tsx before hydration
 */
export function initSentryClient(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || 'production';
  const release = import.meta.env.VITE_SENTRY_RELEASE;

  if (!dsn) {
    // Sentry not configured - skip initialization
    return;
  }

  const options = getSentryClientOptions({
    dsn,
    environment: environment as 'development' | 'staging' | 'production',
    release,
  });

  Sentry.init(options);
}

/**
 * Set user context by shop domain
 * Call when shop is identified (e.g., in root loader)
 */
export function setShopContext(shopDomain: string): void {
  if (!shopDomain) return;

  Sentry.setUser({
    username: shopDomain,
  });

  Sentry.setTag('shop_domain', shopDomain);
}

/**
 * Clear user context
 * Call on logout or session end
 */
export function clearShopContext(): void {
  Sentry.setUser(null);
}

/**
 * Capture exception with context
 * Use for manual error reporting alongside automatic capture
 */
export function captureException(
  error: Error | unknown,
  context?: Record<string, unknown>
): void {
  Sentry.withScope((scope) => {
    if (context) {
      const { shopId, operation, shopDomain, ...extras } = context as {
        shopId?: number | string;
        operation?: string;
        shopDomain?: string;
        [key: string]: unknown;
      };

      // Set indexed tags (matches backend pattern)
      if (shopId) scope.setTag('shop_id', String(shopId));
      if (operation) scope.setTag('operation', String(operation));
      if (shopDomain) scope.setTag('shop_domain', String(shopDomain));

      if (Object.keys(extras).length > 0) {
        scope.setExtras(extras);
      }
    }

    Sentry.captureException(error);
  });
}

/**
 * Add breadcrumb for debugging trail
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
    timestamp: Date.now() / 1000,
  });
}

// Re-export Sentry for direct access when needed
export { Sentry };
